import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBilling } from "../context/BillingContext";
import { appleIapAvailable, purchaseApplePremium, getAppleProductInfo, restoreApplePurchases } from "../lib/appleIap";
import { nativeCtaLabel, nativePriceLine, nativeMainPrice, hasFreeTrialOffer } from "../lib/appleOffer";
import { StripeLinkRestore } from "./More";
import { api, apiError, isAuthError, RETURN_TO_KEY } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Check, ShieldCheck, LogOut } from "lucide-react";

const FEATURES = [
  "Track expected earnings across every practice",
  "Reconcile payments, expenses, GST, tax & super",
  "Accountant pack, BAS summary and reports",
];
const CANCEL_FEATURE = { web: "Cancel anytime from your billing portal", native: "Cancel anytime in your Apple Account settings" };

// Clear, specific restore failures; never a generic success.
export function restoreErrorMessage(e) {
  if (e?.code === "ERR_NETWORK" || e?.message === "Network Error") return "Couldn't reach Molaroo. Check your connection and try again.";
  if (e?.response?.status === 400) return "We couldn't verify this App Store purchase. Nothing was changed.";
  return apiError(e);
}

export default function Paywall() {
  const navigate = useNavigate();
  const { user, logout, expireSession } = useAuth();
  const { billing, loading: billingLoading, refresh } = useBilling();
  const [loading, setLoading] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const native = appleIapAvailable();
  const [product, setProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(native);

  useEffect(() => {
    sessionStorage.removeItem(RETURN_TO_KEY);
    if (!native) return;
    getAppleProductInfo().then((p) => { setProduct(p); setProductLoading(false); });
  }, [native]);

  // Session rejected mid-purchase: re-authenticate, then come back here (no auto-purchase).
  const handleError = async (e) => {
    if (isAuthError(e)) { await expireSession("/paywall"); navigate("/login", { replace: true }); return; }
    if (e?.code === "CANCELLED") return;
    toast.error(apiError(e));
  };

  const exitPreview = async () => {
    setExiting(true);
    try { await api.post("/billing/test/paywall", { lock: false }); await refresh(); navigate("/more"); }
    catch (e) { toast.error(apiError(e)); }
    setExiting(false);
  };

  const price = ((billing?.plan_amount_cents ?? 1499) / 100).toFixed(2);
  const currency = billing?.plan_currency || "AUD";
  const inTrial = billing?.in_trial;

  const subscribe = async () => {
    setLoading(true);
    try {
      if (appleIapAvailable()) {
        // iOS: present the native StoreKit 2 subscription sheet and verify server-side.
        await purchaseApplePremium();
        await refresh();
        toast.success("Subscription active — thank you!");
        navigate("/");
        return;
      }
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { await handleError(e); setLoading(false); }
  };

  const restore = async () => {
    setRestoring(true);
    try {
      const n = await restoreApplePurchases();
      await refresh();
      if (n > 0) { toast.success("Purchases restored"); navigate("/"); }
      else toast.error("No active App Store subscription found for this Apple Account.");
    } catch (e) {
      if (isAuthError(e)) await handleError(e);
      else toast.error(restoreErrorMessage(e));
    }
    setRestoring(false);
  };

  const features = [...FEATURES, native ? CANCEL_FEATURE.native : CANCEL_FEATURE.web];
  const main = native ? nativeMainPrice(product, price, currency) : { amount: `$${price}`, currency };
  const statusUnavailable = native && !billingLoading && !billing;
  const ctaDisabled = loading || (native && (billingLoading || productLoading || statusUnavailable));

  return (
    <div className="df-shell df-safe-top min-h-screen flex flex-col justify-center px-6 py-10">
      <div className="df-fade-in w-full max-w-md mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <img src="/molaroo-mark.png" alt="Molaroo" className="h-14 w-14 rounded-2xl object-contain bg-white shadow-sm" />
          <div>
            <div className="text-2xl font-heading font-bold tracking-tight text-slate-900 leading-none">Molaroo</div>
            <div className="text-sm text-slate-500 mt-1">Your income, organised.</div>
            <div className="text-xs text-slate-400 mt-0.5">by NavScora</div>
          </div>
        </div>

        <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1" data-testid="paywall-title">
          {inTrial ? "You're on a free trial" : native ? "Your Molaroo trial has ended" : "Your free trial has ended"}
        </h1>
        <p className="text-slate-500 mb-6">
          {inTrial
            ? `${billing?.trial_days_left} day${billing?.trial_days_left === 1 ? "" : "s"} left. Subscribe any time to keep going without interruption.`
            : "Subscribe to continue tracking your income and payments."}
        </p>

        <div className="df-card p-6">
          <div className="flex items-baseline gap-1">
            <span className="font-num font-bold text-4xl tracking-tight text-slate-900" data-testid="paywall-price">{main.amount}</span>
            <span className="text-slate-500">{main.currency ? `${main.currency} ` : ""}/ month</span>
          </div>
          <ul className="mt-5 space-y-2.5">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                <span className="h-5 w-5 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center shrink-0 mt-0.5"><Check size={13} /></span>
                {f}
              </li>
            ))}
          </ul>
          {statusUnavailable && (
            <div className="mt-5 rounded-xl bg-amber-50 text-amber-800 text-sm p-3" data-testid="paywall-status-unavailable">
              We couldn't confirm your subscription status.
              <button data-testid="paywall-status-retry" onClick={refresh} className="ml-1 font-semibold underline df-pressable">Retry</button>
            </div>
          )}
          <Button data-testid="paywall-subscribe" onClick={subscribe} disabled={ctaDisabled}
            className="w-full h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] text-base font-semibold mt-6 df-pressable">
            {loading ? "Processing…" : native ? (productLoading ? "Loading…" : nativeCtaLabel(product)) : inTrial ? "Subscribe now" : "Subscribe to continue"}
          </Button>
          {native && !inTrial && hasFreeTrialOffer(product) && (
            <p className="text-xs text-slate-500 mt-2 text-center" data-testid="paywall-intro-note">
              This free period is Apple's introductory offer for your Apple Account, separate from the Molaroo trial.
            </p>
          )}
          {native && (
            <button data-testid="paywall-restore" onClick={restore} disabled={restoring || statusUnavailable}
              className="w-full text-sm text-[#0D786A] font-medium mt-3 df-pressable">
              {restoring ? "Restoring…" : "Restore purchases"}
            </button>
          )}
          {inTrial && (
            <button data-testid="paywall-continue-trial" onClick={() => navigate("/")}
              className="w-full text-sm text-slate-500 mt-3 df-pressable">Keep using my trial</button>
          )}
          <div className="mt-4 pt-4 border-t border-slate-100 text-[11px] text-slate-500 leading-relaxed" data-testid="subscription-disclosure">
            {native ? (
              <p><strong>{nativePriceLine(product, `${currency} $${price}`)}</strong> Billed to your Apple Account. The subscription renews automatically each month unless cancelled at least 24 hours before the end of the current period. Manage or cancel your subscription in your Apple Account under Settings → your name → Subscriptions.</p>
            ) : (
              <p><strong>AUD $14.99 / month.</strong> 14-day free trial — no payment method required to start. You'll only be asked for a card if you choose to subscribe, and billing begins when you subscribe. The subscription renews monthly until you cancel. Cancel anytime from More → Subscription (access continues to the end of the paid period; future charges stop).</p>
            )}
            <p className="mt-1.5">See our <Link to="/terms" className="text-[#0D786A] font-medium">Terms &amp; Conditions</Link> and <Link to="/privacy" className="text-[#0D786A] font-medium">Privacy Policy</Link>.</p>
          </div>
        </div>

        {!native && user?.is_owner && billing && !billing.has_customer && <StripeLinkRestore onDone={refresh} />}

        {billing?.test_paywall && (
          <button data-testid="paywall-exit-preview" onClick={exitPreview} disabled={exiting}
            className="w-full text-sm text-[#0D786A] font-semibold mt-4 df-pressable">
            {exiting ? "Exiting…" : "Exit paywall preview (testing)"}
          </button>
        )}

        <div className="mt-5 flex items-center justify-center gap-4 text-xs text-slate-400">
          <span className="flex items-center gap-1"><ShieldCheck size={13} /> {native ? "Secure purchase via the App Store" : "Secure checkout by Stripe"}</span>
          <button data-testid="paywall-signout" onClick={logout} className="flex items-center gap-1 df-pressable hover:text-slate-600">
            <LogOut size={13} /> Sign out
          </button>
        </div>
        <p className="text-center text-[11px] text-slate-300 mt-4">Molaroo · by NavScora</p>
      </div>
    </div>
  );
}
